import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Select } from 'antd';
import { resolveCampaignDateRange } from '../../../../../../../core/utils/campaignDateRange';

// Global Styling
import {
  CardTitleText,
  MenuItemTextColor,
} from '../../../../../../shared/globalStyling/styledText';
import TransactionsTable from './TransactionsTable';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import TransactionsChartV2 from './charts/TransactionChartV2';
import TransactionsKpiBar from '../../../components/TransactionsKpiBar';
import CampaignRangeSelector from '../../../components/CampaignRangeSelector';
import { computeROAS, computeSpend, getEffectiveCpm } from '../../../../../../../core/utils/campaigns';
import {
  attributionWindowSelections,
  attributionTypeSelections,
} from '../../../../../../../core/utils/constants/constants';
import { cloneDeep } from 'lodash';
import { HasAccess } from '@permify/react-role';
import TransactionCreativeDetails from './transactionCreatives/TransactionCreativeDetails';
import TransactionPublisherDetails from './transactionPublishers/TransactionPublisherDetails';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';

const { Option } = Select;

const Style = {
  campaignCardStyle: {
    margin: 5,
    padding: 2,
    height: 340,
    backgroundColor: '#fff',
  },
  campaignCardStyleCreatives: {
    backgroundColor: '#fff',
  },
  keyColors: {
    spent: '#1665d8',
    remaining: '#34aa44',
    budget: '#030303',
    total: '#bec0c3',
    value: '#99999',
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  xs: {
    span: 24,
    offset: 0,
  },
  sm: {
    span: 24,
    offset: 0,
  },
  md: {
    span: 16,
    offset: 0,
  },
  lg: {
    span: 16,
    offset: 0,
  },
  xl: {
    span: 16,
    offset: 0,
  },
  xsPie: {
    span: 24,
    offset: 0,
  },
  smPie: {
    span: 24,
    offset: 0,
  },
  mdPie: {
    span: 8,
    offset: 0,
  },
  lgPie: {
    span: 8,
    offset: 0,
  },
  xlPie: {
    span: 8,
    offset: 0,
  },
};

const { campaignCardStyleCreatives } = Style;

const TransactionDetailBarV2 = ({
  selectedCampaign,
  chartDataRaw,
  campaignOrder,
  transactionTarget,
  dateTrue,
  priceVisible,
  reportName,
  setDataAttributionWindow,
  transactionsTable,
  transactionsTableLoading,
  setCurrentIdx,
  attributionWindow,
  setAttributionWindow,
  currentUser,
  currencyFormat,
  embeddableConfig,
  attributionMethod,
  setAttributionMethod,
  setCurrentStartEndDate,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const sitesAppsTransactions = currentUser.permission.sitesAppsTransactions
    ? currentUser.permission.sitesAppsTransactions
    : false;
  const { startDate, endDate } = selectedCampaign ? selectedCampaign : [];
  const { type, transactionsConfig } = campaignOrder;
  const [transactionData, setTransactionData] = useState([]);
  const [byDate, setByDate] = useState([]);

  let getDate =
    chartDataRaw && chartDataRaw.byDate
      ? cloneDeep(chartDataRaw.byDate).map((a) => moment(a.xAxis, 'MM/DD/YYYY'))
      : [];

  // sort them by desc dates
  getDate = !isEmpty(getDate) && getDate.sort((a, b) => a.unix() - b.unix());
  const [dateFilter, setDateFilter] = useState([
    getDate && getDate.length && getDate[0],
    getDate && getDate.length && getDate.slice(-1)[0],
  ]);
  const initialSpend = computeSpend(0, selectedCampaign ? getEffectiveCpm(campaignOrder) : 0);
  const [detailBarValues, setDetailBarValues] = useState({
    startDate,
    endDate,
    transactions: 0,
    transactionRevenue: 0,
    customers: 0,
    impressions: 0,
    spend: initialSpend,
    roas: computeROAS(0, initialSpend),
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [creativeDimension, setCreativeDimension] = useState('impressions');
  const [publisherDimension, setPublisherDimension] = useState('impressions');

  // Date-range preset for the unified Performance card header. Starts at
  // 'Custom' because initial load shows the full data range (min→max), which
  // is a custom span, not a preset. Label stays descriptive because the
  // CampaignRangeSelector falls back to defaultDateRange text for 'Custom'.
  const [rangeType, setRangeType] = useState('Custom');

  // Campaign flight start → today (see core/utils/campaignDateRange). Row dates
  // here are 'MM/DD/YYYY', so normalise before handing them over — the resolver
  // parses ISO / 'YYYY-MM-DD'.
  const defaultDateRange = useMemo(() => {
    const rows = (chartDataRaw && chartDataRaw.byDate) || [];
    return resolveCampaignDateRange(
      campaignOrder,
      rows.map((d) => {
        const parsed = moment(d && d.xAxis, 'MM/DD/YYYY', true);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
      })
    );
  }, [chartDataRaw, campaignOrder]);

  const isSimplified =
    chartDataRaw && chartDataRaw.hasSimplifiedReport ? chartDataRaw.hasSimplifiedReport : false;

  const changeData = (selectedByDate, selectedAttributionWindow, selectedAttributionMethod) => {
    //Clone and isolate date needed to prevent mutation due to state changes
    const byDateChangingData = chartDataRaw.byDate.map((byDate) => {
      byDate.xAxis = moment(byDate.xAxis).format('MM/DD/YYYY');
      return byDate;
    });
    const chartData = cloneDeep(chartDataRaw);
    let detailBarFiltered = {
      startDate,
      endDate,
      transactions: 0,
      transactionRevenue: 0,
      customers: 0,
      impressions: 0,
      spend: 0,
      roas: 0,
    };
    //Filtering of transactions data by attribution window
    let filteredTransactionsWithAttribution = chartData.transactionsWithEvents.filter((trans) => {
      const { transaction, latestDate } = trans;
      const impressionDate = new Date(moment(latestDate).format());
      const outOfRange = new Date(moment(transaction).format());
      outOfRange.setDate(outOfRange.getDate() - selectedAttributionWindow);
      if (impressionDate < outOfRange) {
        return false;
      } else if (impressionDate > outOfRange) {
        return true;
      }
      return false;
    });
    if (selectedAttributionMethod !== 'ALL') {
      filteredTransactionsWithAttribution = filteredTransactionsWithAttribution.filter((a) => {
        const { attributionMethod } = a;
        if (attributionMethod && typeof attributionMethod === 'object') {
          const keys = Object.keys(attributionMethod);
          if (keys.includes(selectedAttributionMethod)) {
            return true;
          }
        } else if (attributionMethod === selectedAttributionMethod) {
          return true;
        }
        return false;
      });
    }
    const filteredTransactions = filteredTransactionsWithAttribution.filter((trans) => {
      return (
        moment(trans.transaction, 'YYYY-MM-DD').isSameOrAfter(
          moment(selectedByDate[0]).format('YYYY-MM-DD'),
          'YYYY-MM-DD'
        ) &&
        moment(trans.transaction, 'YYYY-MM-DD').isSameOrBefore(
          moment(moment(selectedByDate[1]).format('YYYY-MM-DD'), 'YYYY-MM-DD')
        )
      );
    });
    //Generate a new by date object since there is a new set transactions due to attribution change
    const attributedByDate = [];
    filteredTransactions.forEach((t) => {
      const date = moment(t.transaction, 'YYYY-MM-DD').format('MM/DD/YYYY');
      const chartDataIdx = byDateChangingData.findIndex((c) => c.xAxis === date);
      if (attributedByDate.find((c) => c.xAxis === date)) {
        const idx = attributedByDate.findIndex((c) => c.xAxis === date);
        attributedByDate[idx].transactions += 1;
        attributedByDate[idx].transactionRevenue += t.transactionTotal ? t.transactionTotal : 0;
      } else {
        attributedByDate.push({
          transactions: 1,
          impressions: byDateChangingData[chartDataIdx]
            ? byDateChangingData[chartDataIdx].impressions
            : 0, //Import the impressions on respective date in the default chart data
          latestDate: t.latestDate
            ? t.latestDate
            : t.impressions.reduce((acc, curr) => {
                if (moment(curr.IMPRESSION_TIME)) {
                  return moment(curr.IMPRESSION_TIME) > moment(acc.IMPRESSION_TIME) ? curr : acc;
                }
                return acc;
              }).IMPRESSION_TIME,
          transactionRevenue: t.transactionTotal ? t.transactionTotal : 0,
          xAxis: date,
        });
      }
    });
    //The data is being malformed withoud cloning it. The reason for this is unknown
    const attributedByDateFinal = cloneDeep(byDateChangingData).map((b) => {
      const attributedIdx = attributedByDate.findIndex((a) => a.xAxis === b.xAxis);

      const attributed = attributedByDate[attributedIdx];
      if (attributed) {
        b.transactions = attributed.transactions;
        b.transactionRevenue = attributed.transactionRevenue;
      } else {
        b.transactions = 0;
        b.transactionRevenue = 0;
      }
      detailBarFiltered.transactions += b.transactions;
      detailBarFiltered.transactionRevenue += b.transactionRevenue;
      return b;
    });

    byDateChangingData.forEach((b) => {
      if (
        moment(b.xAxis, 'MM/DD/YYYY') >= moment(selectedByDate[0], 'YYYY-MM-DD') &&
        moment(b.xAxis, 'MM/DD/YYYY') <= moment(selectedByDate[1], 'YYYY-MM-DD')
      ) {
        detailBarFiltered.impressions += b.impressions;
      }
    });

    detailBarFiltered.customers = determineUniques(filteredTransactions);
    detailBarFiltered.spend = computeSpend(
      detailBarFiltered.impressions,
      selectedCampaign ? getEffectiveCpm(campaignOrder) : 0
    );
    detailBarFiltered.roas = computeROAS(
      detailBarFiltered.transactionRevenue,
      detailBarFiltered.spend
    );
    setByDate(attributedByDateFinal);
    setTransactionData(filteredTransactions);
    setDataAttributionWindow(filteredTransactions);
    setDetailBarValues(detailBarFiltered);
    setCurrentIdx(0);
    setCurrentPage(1);
    setDateFilter(selectedByDate);
    setAttributionWindow(selectedAttributionWindow);
    setAttributionMethod(selectedAttributionMethod);
  };

  const determineUniques = (data) => {
    const uniques = [];
    data.forEach((item) => {
      const { attributionMethod } = item;

      //format the attribution method to be stringified
      let val = JSON.stringify(attributionMethod);

      if (!uniques.includes(val)) {
        uniques.push(val);
      }
    });

    return uniques.length;
  };

  if (byDate.length === 0 && chartDataRaw.byDate.length > 0) {
    changeData(dateFilter, attributionWindow, attributionMethod);
  }
  useEffect(() => {
    setTransactionData(transactionData);
  }, [transactionData]);

  return (
    <>
      {/* Unified Performance card — KPI bar, range selector, and time-series
          chart read as one card. Mirrors the Summary / Display Performance
          pattern. Attribution window + method controls stay on the
          Transactions table below (Stage 2). */}
      <HasAccess permissions={['default', 'defaultSSO']}>
        <section className="campaign-overview__performance">
          <header className="campaign-overview__performance-head">
            <div className="campaign-overview__performance-eyebrow mono-eyebrow">
              Performance
            </div>
            {chartDataRaw && !isEmpty(chartDataRaw.byDate) && (
              <CampaignRangeSelector
                rangeType={rangeType}
                byCampaignRangeDate={dateFilter}
                setRangeType={setRangeType}
                setByCampaignRangeDate={(newRange) => {
                  changeData(newRange, attributionWindow, attributionMethod);
                  setCurrentStartEndDate(newRange);
                }}
                timezone={effectiveTimezone}
                defaultDateRange={defaultDateRange}
              />
            )}
          </header>

          <TransactionsKpiBar
            data={detailBarValues}
            priceVisible={priceVisible}
            dataConfig={!isEmpty(currentUser.role.org) ? currentUser.role.org.dataConfig : {}}
            currencyFormat={currencyFormat}
          />

          <HasAccess permissions={['default', 'defaultSSO', 'widgetSSOtransactionChart']}>
            {chartDataRaw && !isEmpty(chartDataRaw.byDate) && (
              <TransactionsChartV2 chartData={byDate} />
            )}
          </HasAccess>
        </section>
      </HasAccess>

      <Row>
        <Col span={24}>
          <HasAccess permissions={['default', 'defaultSSO', 'widgetSSOtransactionTable']}>
            {sitesAppsTransactions && (
              <>
                <TransactionCreativeDetails
                  transactionsCreatives={
                    chartDataRaw && chartDataRaw.transactionsCreatives
                      ? chartDataRaw && chartDataRaw.transactionsCreatives
                      : []
                  }
                  creativeDimension={creativeDimension}
                  setCreativeDimension={setCreativeDimension}
                />
                <TransactionPublisherDetails
                  transactionsPublishers={
                    chartDataRaw && chartDataRaw.transactionsPublishers
                      ? chartDataRaw && chartDataRaw.transactionsPublishers
                      : []
                  }
                  publisherDimension={publisherDimension}
                  setPublisherDimension={setPublisherDimension}
                />
              </>
            )}

            <section className="campaign-overview__creatives">
              <header className="campaign-overview__creatives-head">
                <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
                  Transactions
                </div>
                <div className="campaign-overview__creatives-controls">
                  <div className="campaign-overview__creatives-control">
                    <span className="campaign-overview__creatives-control-label mono-eyebrow">
                      Attribution Window
                    </span>
                    <Select
                      placeholder="Please select days"
                      value={attributionWindow}
                      className="campaign-overview__creatives-showby"
                      popupClassName="mjl-ds"
                      getPopupContainer={(trigger) => trigger.parentNode}
                      onChange={(val) => changeData(dateFilter, val, attributionMethod)}
                      size="small"
                      style={{ minWidth: 180 }}
                    >
                      {attributionWindowSelections.map((option) => (
                        <Option key={option.key} value={option.key}>
                          <MenuItemTextColor text={option.name} />
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="campaign-overview__creatives-control">
                    <span className="campaign-overview__creatives-control-label mono-eyebrow">
                      Attribution Method
                    </span>
                    <Select
                      placeholder="Attribution method"
                      value={attributionMethod}
                      className="campaign-overview__creatives-showby"
                      popupClassName="mjl-ds"
                      getPopupContainer={(trigger) => trigger.parentNode}
                      onChange={(val) => changeData(dateFilter, attributionWindow, val)}
                      size="small"
                      style={{ minWidth: 180 }}
                    >
                      {attributionTypeSelections.map((option) => (
                        <Option key={option.key} value={option.key}>
                          <MenuItemTextColor text={option.name} />
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </header>

              <div className="campaign-overview__creatives-body">
                <div className="campaign-overview__creatives-table">
                  <TransactionsTable
                    reportName={reportName}
                    campaignOrderId={campaignOrder.id}
                    transactionData={
                      !isSimplified
                        ? transactionData
                        : transactionsTable
                        ? transactionsTable.record
                        : []
                    }
                    transactionTotalRecord={
                      isSimplified && transactionsTable ? transactionsTable.count : 0
                    }
                    transactionTarget={transactionTarget}
                    type={type}
                    transactionsConfig={transactionsConfig}
                    dateTrue={dateTrue}
                    dateFilter={dateFilter}
                    isSimplified={isSimplified}
                    setCurrentIdx={setCurrentIdx}
                    loading={transactionsTableLoading}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    publisherInfo={
                      chartDataRaw && chartDataRaw.transactionsPublishers
                        ? chartDataRaw && chartDataRaw.transactionsPublishers
                        : []
                    }
                  />
                </div>
              </div>
            </section>
          </HasAccess>
        </Col>
      </Row>
    </>
  );
};

export default TransactionDetailBarV2;
